import { Colors, Lightning, Storage } from "@lightningjs/sdk";
import { List } from "@lightningjs/ui";
import { SubtitleTrack } from "bitmovin-player";
import { TranslatableText } from "../../../../../components/data-display/TranslatableText/TranslatableText";
import { toggleSubtitle, toggleAudio } from "../../../../../lib/video-player";
import theme from "../../../../../lib/theme";
import { Icon } from "../../../../../components/data-display/Icon/Icon";
import { STORAGE_KEYS } from "../../../../../lib/utils";

interface SettingsPopupFullScreenTemplateSpec extends Lightning.Component.TemplateSpec {
    Content: {
        Border: typeof Object;
        Container: {
            SubtitleSection: {
                Title: typeof TranslatableText;
                MenuItems: typeof List;
            };
            AudioSection: {
                Title: typeof TranslatableText;
                MenuItems: typeof List;
            };
        };
    };
}

export class SettingsPopupFullScreen
    extends Lightning.Component<SettingsPopupFullScreenTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<SettingsPopupFullScreenTemplateSpec>
{
    private static readonly POPUP_WIDTH = 1125;
    private static readonly POPUP_HEIGHT = 700;
    private static readonly SECTION_PADDING = 64;
    private static readonly GAP = 108;
    private static readonly SECTION_WIDTH = this.POPUP_WIDTH / 2 - this.SECTION_PADDING - this.GAP / 2;
    private static readonly SECTION_HEIGHT = this.POPUP_HEIGHT - 84;

    private _subtitleMenuItems: {
        type: typeof MenuItem;
        label: string;
        value: string;
        menuSignal: string;
        icon: any;
        data?: SubtitleTrack;
    }[] = [];
    private _audioMenuItems: {
        type: typeof MenuItem;
        label: string;
        value: string;
        menuSignal: string;
        icon: any;
    }[] = [];
    private _subtitlePreference: string = Storage.get(STORAGE_KEYS.preferredSubtitle);
    private _audioPreference: string = Storage.get(STORAGE_KEYS.preferredAudio);

    _subtitleIndex = 0;
    _audioIndex = 0;
    _index = 0; // For compatibility with wrapper
    currentMenu = "main"; // For compatibility with wrapper

    readonly Content = this.getByRef("Content")!;
    readonly Container = this.Content.getByRef("Container")!;
    readonly SubtitleSection = this.Container.getByRef("SubtitleSection")!;
    readonly AudioSection = this.Container.getByRef("AudioSection")!;
    readonly SubtitleMenuItems = this.SubtitleSection.getByRef("MenuItems")!;
    readonly AudioMenuItems = this.AudioSection.getByRef("MenuItems")!;

    _setSubtitlePreference(lang: string) {
        Storage.set(STORAGE_KEYS.preferredSubtitle, lang);
        console.log(`Preferred subtitle set to: ${lang}`);
        this._subtitlePreference = lang;
    }

    _setAudioPreference(lang: string) {
        Storage.set(STORAGE_KEYS.preferredAudio, lang);
        console.log(`Preferred audio set to: ${lang}`);
        this._audioPreference = lang;
    }

    static override _template(): Lightning.Component.Template<SettingsPopupFullScreenTemplateSpec> {
        const popupWidth = SettingsPopupFullScreen.POPUP_WIDTH;
        const popupHeight = SettingsPopupFullScreen.POPUP_HEIGHT;
        const sectionPadding = SettingsPopupFullScreen.SECTION_PADDING;
        const gap = SettingsPopupFullScreen.GAP;
        const sectionWidth = SettingsPopupFullScreen.SECTION_WIDTH;
        const sectionHeight = SettingsPopupFullScreen.SECTION_HEIGHT;

        return {
            w: theme.layout.w,
            h: theme.layout.h,
            rect: true,
            color: Colors(theme.colors.bg.overlay).alpha(0.75).get(),
            alpha: 0,
            zIndex: 20,
            collision: true,
            Content: {
                w: popupWidth,
                h: popupHeight,
                x: (theme.layout.w - popupWidth) / 2,
                y: (theme.layout.h - popupHeight) / 2,
                collision: true,
                rect: true,
                color: Colors(theme.colors.bg.secondary).get(),
                shader: {
                    type: Lightning.shaders.RoundedRectangle,
                    radius: 10
                },
                Border: {
                    rect: true,
                    color: Colors(theme.colors.text.primary).alpha(50).get(),
                    w: 2,
                    h: popupHeight - 120,
                    y: 60,
                    x: popupWidth / 2 - 0.5
                },
                Container: {
                    x: sectionPadding,
                    y: 42,
                    w: popupWidth - sectionPadding * 2,
                    h: popupHeight - 90,
                    flex: {
                        direction: "row",
                        justifyContent: "space-between",
                        alignItems: "flex-start"
                    },
                    SubtitleSection: {
                        w: sectionWidth,
                        h: sectionHeight,
                        flex: {
                            direction: "column",
                            alignItems: "stretch"
                        },
                        Title: {
                            y: 0,
                            x: 0,
                            w: sectionWidth,
                            h: 50,
                            type: TranslatableText,
                            text: {
                                fontSize: 32,
                                lineHeight: 34,
                                fontFace: "Medium",
                                textAlign: "left",
                                textColor: Colors(theme.colors.text.primary).get()
                            }
                        },
                        MenuItems: {
                            y: 60,
                            x: 0,
                            collision: true,
                            w: sectionWidth,
                            type: List,
                            direction: "column"
                        }
                    },
                    AudioSection: {
                        w: sectionWidth,
                        h: sectionHeight,
                        flex: {
                            direction: "column",
                            alignItems: "stretch"
                        },
                        flexItem: {
                            marginLeft: gap
                        },
                        Title: {
                            y: 0,
                            x: 0,
                            w: sectionWidth,
                            h: 50,
                            type: TranslatableText,
                            text: {
                                fontSize: 32,
                                lineHeight: 34,
                                fontFace: "Medium",
                                textAlign: "left",
                                textColor: Colors(theme.colors.text.primary).get()
                            }
                        },
                        MenuItems: {
                            y: 60,
                            x: 0,
                            collision: true,
                            w: sectionWidth,
                            type: List,
                            direction: "column"
                        }
                    }
                }
            }
        };
    }

    override _active() {
        this._updateMenuItems();
    }

    override _inactive() {
        this.setSmooth("alpha", 0);
    }

    open() {
        this.setSmooth("alpha", 1);
        this._setState("SubtitleState");
        this._updateMenuItems();
    }

    close() {
        this.setSmooth("alpha", 0);
    }

    static override _states() {
        return [
            class SubtitleState extends this {
                override _getFocused() {
                    return this.SubtitleMenuItems;
                }

                override _handleRight() {
                    this._setState("AudioState");
                }

                override _handleEnter() {
                    const focusedItem = this.SubtitleMenuItems.currentItem;

                    if (!focusedItem) return;

                    const value = focusedItem.value;
                    if (focusedItem && focusedItem.menuSignal) {
                        switch (focusedItem.menuSignal) {
                            case "onSubtitleSelect":
                                this._setSubtitlePreference(value == "off" ? "" : value);
                                toggleSubtitle(value);
                                if (value == "off") {
                                    this.fireAncestors("$clearSubtitleText");
                                }
                                // this._updateMenuItems();

                                for (const [index, item] of Object.entries(this.SubtitleMenuItems.items)) {
                                    item.patch({
                                        selected: +index == this.SubtitleMenuItems.index
                                    });
                                }
                                break;
                            default:
                                this.signal(focusedItem.menuSignal);
                                break;
                        }
                    }
                }
            },

            class AudioState extends this {
                override _getFocused() {
                    return this.AudioMenuItems;
                }

                override _handleLeft() {
                    this._setState("SubtitleState");
                }

                override _handleEnter() {
                    const focusedItem = this.AudioMenuItems.currentItem;

                    if (!focusedItem) return;

                    const value = focusedItem.value;

                    if (focusedItem && focusedItem.menuSignal) {
                        switch (focusedItem.menuSignal) {
                            case "onAudioSelect":
                                this._setAudioPreference(value);
                                toggleAudio(value);
                                // this._updateMenuItems();
                                for (const [index, item] of Object.entries(this.AudioMenuItems.items)) {
                                    item.patch({
                                        selected: +index === this.AudioMenuItems.index
                                    });
                                }
                                break;
                            default:
                                this.signal(focusedItem.menuSignal);
                                break;
                        }
                    }
                }
            }
        ];
    }

    override _handleBack() {
        this.fireAncestors("$hideSettingsPopup");
    }

    private _updateMenuItems() {
        // Update subtitle menu
        if (this.SubtitleSection && this.SubtitleSection.getByRef("Title")) {
            this.SubtitleSection.getByRef("Title")!.patch({
                key: "settings.popup.subtitleMenuTitle"
            });
        }

        this._subtitleIndex = this._subtitlePreference
            ? this._subtitleMenuItems.findIndex((s) => s.value === this._subtitlePreference)
            : 0;
        if (this._subtitleIndex < 0) this._subtitleIndex = 0;

        const subtitleItems = this._subtitleMenuItems.map((s, i) => ({
            ...s,
            selected: this._subtitleIndex === i,
            signals: {
                onHover: "_handleHover"
            }
        }));

        if (this.SubtitleMenuItems) {
            if (subtitleItems.length > 0) {
                this.SubtitleMenuItems.clear();
                this.SubtitleMenuItems.add(subtitleItems);
                this.SubtitleMenuItems.setIndex(this._subtitleIndex);
                // Set collision on ListWrapper children
                for (const wrapper of this.SubtitleMenuItems.children) {
                    wrapper?.patch({
                        collision: true
                    });
                }
            } else {
                this.SubtitleMenuItems.clear();
            }
        }

        // Update audio menu
        if (this.AudioSection && this.AudioSection.getByRef("Title")) {
            this.AudioSection.getByRef("Title")!.patch({
                key: "settings.popup.audioMenuTitle"
            });
        }

        this._audioIndex = this._audioPreference
            ? this._audioMenuItems.findIndex((a) => a.value === this._audioPreference)
            : 0;
        if (this._audioIndex < 0) this._audioIndex = 0;

        const audioItems = this._audioMenuItems.map((a, i) => ({
            ...a,
            selected: this._audioIndex === i,
            signals: {
                onHover: "_handleHover"
            }
        }));

        console.log("audioItems", audioItems);
        if (this.AudioMenuItems) {
            if (audioItems.length > 0) {
                this.AudioMenuItems.clear();
                this.AudioMenuItems.add(audioItems);
                this.AudioMenuItems.setIndex(this._audioIndex);
                // Set collision on ListWrapper children
                for (const wrapper of this.AudioMenuItems.children) {
                    wrapper?.patch({
                        collision: true
                    });
                }
            } else {
                this.AudioMenuItems.clear();
            }
        }

        this._refocus();
    }

    setAvailableSubtitles(subtitles: SubtitleTrack[]) {
        const sectionWidth = SettingsPopupFullScreen.SECTION_WIDTH || 444;
        let subtitleMenuItems = [
            {
                type: MenuItem,
                label: "settings.popup.menuItemOff",
                value: "off",
                w: sectionWidth,
                menuSignal: "onSubtitleSelect",
                icon: null,
                h: 44,
                selected: this._subtitlePreference == "" || subtitles.length === 0
            }
        ];

        subtitleMenuItems = subtitleMenuItems.concat(
            subtitles.map((sub) => ({
                type: MenuItem,
                label: sub.label,
                value: sub.lang,
                w: sectionWidth,
                h: 44,
                menuSignal: "onSubtitleSelect",
                icon: null,
                data: JSON.parse(JSON.stringify(sub)),
                selected: false
            }))
        );
        this._subtitleMenuItems = subtitleMenuItems;

        this._updateMenuItems();
    }

    setSubtitlePreference(lang: string) {
        this._subtitlePreference = lang;
    }

    setAvailableAudioLanguages(languages: Array<{ language: string; languageName: string }>) {
        const sectionWidth = SettingsPopupFullScreen.SECTION_WIDTH || 444;
        const audioMenuItems = languages.map((lang) => ({
            type: MenuItem,
            label: lang.languageName || lang.language,
            value: lang.language,
            w: sectionWidth,
            h: 44,
            menuSignal: "onAudioSelect",
            icon: null,
            arrow: null,
            hide: true,
            selected: false
        }));

        this._audioMenuItems = audioMenuItems;

        // Set default preference if not set
        if (!this._audioPreference && languages.length > 0) {
            const preferredAudio = Storage.get(STORAGE_KEYS.preferredAudio);
            if (preferredAudio && languages.some((l) => l.language === preferredAudio)) {
                this._audioPreference = preferredAudio;
            } else {
                this._audioPreference = languages[0]!.language;
            }
        }

        this._updateMenuItems();
    }

    setAudioPreference(lang: string) {
        this._audioPreference = lang;
    }

    _handleHover(hoveredItem: MenuItem) {
        // Find which list contains this item by checking ListWrapper children
        const subtitleWrapperIndex = this.SubtitleMenuItems.children.findIndex((wrapper: any) => {
            return wrapper?.children?.[0] === hoveredItem;
        });
        const audioWrapperIndex = this.AudioMenuItems.children.findIndex((wrapper: any) => {
            return wrapper?.children?.[0] === hoveredItem;
        });

        if (subtitleWrapperIndex !== -1) {
            this._subtitleIndex = subtitleWrapperIndex;
            if (this._getState() !== "SubtitleState") {
                this._setState("SubtitleState");
            }
            this.SubtitleMenuItems.setIndex(subtitleWrapperIndex);
            this._refocus();
        } else if (audioWrapperIndex !== -1) {
            this._audioIndex = audioWrapperIndex;
            if (this._getState() !== "AudioState") {
                this._setState("AudioState");
            }
            this.AudioMenuItems.setIndex(audioWrapperIndex);
            this._refocus();
        }
    }
}

interface MenuItemTemplateSpec extends Lightning.Component.TemplateSpec {
    Icon: typeof Icon;
    Label: typeof TranslatableText;
}

class MenuItem
    extends Lightning.Component<MenuItemTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<MenuItemTemplateSpec>
{
    menuSignal?: string;
    _value!: string;

    static override _template(): Lightning.Component.Template<MenuItemTemplateSpec> {
        return {
            w: 444,
            h: 72,
            zIndex: 20,
            rect: true,
            flex: {
                direction: "row",
                alignItems: "center"
                // paddingLeft: 20,
                // paddingRight: 20
            },
            flexItem: { grow: 1, marginBottom: 16 },
            color: Colors(theme.colors.bg.secondary).get(),
            collision: true,
            Icon: {
                type: Icon,
                iconWidth: 32,
                w: 32,
                h: 32,
                collision: true
            },
            Label: {
                flexItem: {
                    marginLeft: 16
                },
                y: 2,
                type: TranslatableText,
                collision: true,
                text: {
                    text: "",
                    fontSize: 24,
                    lineHeight: 26,
                    fontFace: "Medium",
                    textAlign: "left",
                    textColor: Colors(theme.colors.text.primary).get()
                }
            }
        };
    }

    set label(value: string) {
        this.tag("Label")!.patch({
            key: value
        });
    }

    set icon(value: string) {
        if (value) {
            this.tag("Icon")!.icon = value;
        }
    }

    set value(value: string) {
        this._value = value;
    }

    get value() {
        return this._value;
    }

    override _focus() {
        this.patch({
            smooth: {
                color: Colors(theme.colors.focusAccent).get()
            }
        });
    }

    override _unfocus() {
        this.patch({
            smooth: { color: Colors(theme.colors.bg.secondary).get() }
        });
    }

    set selected(value: boolean) {
        this.patch({
            Icon: {
                icon: "check",
                alpha: value ? 1 : 0.01
            }
        });
    }

    _handleHover(e: any) {
        this.signal("onHover", this);
    }
}
